import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import axiosInstant from "../../api";
import config from "../../config";
import { TOKEN } from "../../store/constant";
import { AuthCard } from "../authV2/components/constants";
import { BasicPage } from "../authV2/components";

// Sign-in handoff for the internal docs site (tdc-documentation).
// The docs send an unsigned-in visitor here with ?return=<docs URL>. A
// signed-in internal user gets a one-time code from the API, which goes back
// to the docs in the URL fragment (never sent to a server); the docs trade it
// for the site key. Not signed in -> normal login, then back here.

type Status = "working" | "forbidden" | "unavailable" | "badReturn" | "error";

const MESSAGES: Record<Exclude<Status, "working">, string> = {
  forbidden: "Your account doesn't have access to the TDC documentation. It's for internal team members.",
  unavailable: "Documentation sign-in isn't set up yet. Please try again later.",
  badReturn: "This sign-in link isn't valid. Open the documentation and try again.",
  error: "Something went wrong signing you in to the documentation. Please try again.",
};

const allowedReturn = (raw: string | null): URL | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const origins = (config.DOCS_ORIGINS as string[] | undefined) || [];
    return origins.includes(url.origin) ? url : null;
  } catch {
    return null;
  }
};

export function DocsSsoPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("working");

  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
    };

    const params = new URLSearchParams(window.location.search);
    const returnUrl = allowedReturn(params.get("return"));
    if (!returnUrl) {
      setStatus("badReturn");
      return cleanup;
    }

    if (!localStorage.getItem(TOKEN)) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
      return cleanup;
    }

    axiosInstant
      .post("/docs/sso-code")
      .then((res) => {
        if (cancelled) return;
        const code = res.data?.code;
        if (!code) {
          setStatus("error");
          return;
        }
        const back = returnUrl.pathname + returnUrl.search;
        window.location.replace(
          `${returnUrl.origin}/__gate/#code=${encodeURIComponent(code)}&return=${encodeURIComponent(back)}`,
        );
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        const httpStatus = err?.response?.status;
        // 401 is handled by the API client (logout -> login -> back here).
        if (httpStatus === 401) return;
        if (httpStatus === 403) setStatus("forbidden");
        else if (httpStatus === 503) setStatus("unavailable");
        else setStatus("error");
      });
    return cleanup;
  }, [navigate]);

  return (
    <BasicPage>
      <AuthCard elevation={0} variant="outlined">
        <Box textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={3}>
          <Typography variant="h5">TDC Documentation</Typography>
          {status === "working" ? (
            <>
              <CircularProgress size={28} />
              <Typography color="text.secondary">Signing you in to the documentation…</Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary">{MESSAGES[status]}</Typography>
              <Button variant="contained" onClick={() => navigate("/", { replace: true })}>
                Back to the portal
              </Button>
            </>
          )}
        </Box>
      </AuthCard>
    </BasicPage>
  );
}
